import React, { useMemo } from 'react';
import { connect, shallowEqual, useSelector } from 'react-redux';
import classNames from 'classnames';
import { Box } from '@mui/material';
import { getEntitlements, getUserDetails, getUserSettings } from '@surfline/web-common';

import CamCompanionAd from 'components/CamCompanionAd';
import NearbySpots from 'components/NearbySpots';
import SpotKbygRewinds from 'components/SpotKbygRewinds';
import SpotCurrentConditions from 'containers/SpotCurrentConditions';
import {
  useMaxWidthDesktopMedium,
  useMaxWidthTabletLarge,
  useMinWidthTablet,
} from 'hooks/useMediaQueries';
import {
  getAdvertisingIds,
  getSpotDetails,
  getSpotReport,
  getSpotReportAssociated,
  getSpotReportForecast,
  getSpotReportNotes,
} from 'selectors/spot';
import { getSubregionAssociated } from 'selectors/subregion';
import { getUserIsEntitled, useUserPermissionStatus } from 'selectors/user';
import type { AppState } from 'stores';
import type {
  SpotDetails,
  SpotForecast,
  SpotReport as TSpotReport,
  SpotReportAssociated,
  SpotReportNotes,
} from 'types/spot';
import { SL_WEB_FORECAST_GRAPHS_MODERNIZATION } from 'common/treatments';
import { getIsPremiumWithAdsUser } from 'utils/user';
import { useTreatments } from 'utils/treatments';

import styles from './SpotCurrentConditionsSection.module.scss';

const spotCurrentConditionsSectionClass = (
  hasCam: boolean,
  hasReport: boolean,
  isPremium: boolean,
  isMultiCam: boolean,
) =>
  classNames({
    [styles.currentConditions]: true,
    [styles.currentConditionsWithCam]: hasCam,
    [styles.currentConditionsNoCam]: !hasCam,
    [styles.currentConditionsWithReport]: hasReport,
    [styles.currentConditionsNoReport]: !hasReport,
    [styles.currentConditionsPremium]: isPremium,
    [styles.currentConditionsFree]: !isPremium,
    [styles.currentConditionsMultiCam]: isMultiCam,
    [styles.currentConditionsSingleCam]: hasCam && !isMultiCam,
    'sl-spot-current-conditions-section--theatre-mode': true,
  });

interface Props {
  isEntitled?: boolean;
  report: TSpotReport['report'];
  reportAssociated: SpotReportAssociated;
  reportData: SpotForecast;
  reportNotes: SpotReportNotes;
  spot: SpotDetails;
  spotId: string;
  onOpenRateTheSurf?: () => void;
}

const SpotCurrentConditionsSection: React.FC<Props> = ({
  isEntitled = false,
  report,
  reportAssociated,
  reportData,
  reportNotes,
  spot,
  spotId,
  onOpenRateTheSurf,
}) => {
  const hasCam = spot.cameras.length > 0;
  const isMultiCam = spot.cameras.length > 1;
  const hasReport = !!report;
  const isMaxWidthDesktopMd = useMaxWidthDesktopMedium();
  const isMaxWidthTabletLg = useMaxWidthTabletLarge();
  const isGreaterThanTablet = useMinWidthTablet();
  const { hasPremiumCamPermissions, hasAdFreePermissions } = useUserPermissionStatus();
  const entitlements = useSelector(getEntitlements, shallowEqual);
  const isPremiumWithAdsUser = getIsPremiumWithAdsUser(entitlements);
  const treatments = useTreatments();
  const isGraphs202603 = treatments?.[SL_WEB_FORECAST_GRAPHS_MODERNIZATION] === 'on';

  const showCompanionAd = useMemo(() => {
    if (hasAdFreePermissions) return false;
    if (isPremiumWithAdsUser || !hasPremiumCamPermissions)
      return isMaxWidthDesktopMd || isMaxWidthTabletLg;
    return false;
  }, [
    hasAdFreePermissions,
    hasPremiumCamPermissions,
    isPremiumWithAdsUser,
    isMaxWidthDesktopMd,
    isMaxWidthTabletLg,
  ]);

  return (
    <div className={spotCurrentConditionsSectionClass(hasCam, hasReport, isEntitled, isMultiCam)}>
      {hasCam && <SpotKbygRewinds cameras={spot.cameras} spot={spot} />}
      <div className={styles.noCamNearbySpots}>
        <div className="sl-section-container">
          <NearbySpots spotId={spotId} />
        </div>
      </div>
      {showCompanionAd && (
        <CamCompanionAd
          adConfigName="camCompanionAdBelowCam"
          key={isGreaterThanTablet ? 'desktop' : 'tablet'}
          style={{
            marginTop: '10px',
          }}
        />
      )}
      <Box
        className={classNames('sl-current-conditions', styles.currentConditionsV2Wrapper, {
          [styles.graphs202603]: isGraphs202603,
        })}
      >
        <div
          className={classNames('sl-section-container', styles.currentConditionsV2ContentContainer)}
        >
          <SpotCurrentConditions
            report={report}
            reportAssociated={reportAssociated}
            reportData={reportData}
            reportNotes={reportNotes}
            spot={spot}
            onOpenRateTheSurf={onOpenRateTheSurf}
          />
        </div>
      </Box>
    </div>
  );
};

export default connect((state: AppState) => ({
  advertisingIds: getAdvertisingIds(state),
  isEntitled: getUserIsEntitled(state),
  report: getSpotReport(state),
  reportAssociated: getSpotReportAssociated(state),
  reportData: getSpotReportForecast(state),
  reportNotes: getSpotReportNotes(state),
  spot: getSpotDetails(state),
  subregionAssociated: getSubregionAssociated(state),
  userDetails: getUserDetails(state),
  userSettings: getUserSettings(state),
}))(SpotCurrentConditionsSection);
